// WebGPU 실행 중 실패 → WebGL2로 다시 열기(L1029 렌더 레인).
// 부팅 시험(gpuProbe)을 통과해도 실제 장면 셰이더에서 드라이버·브라우저 버전에 따라 파이프라인 생성이 실패할 수 있다
// (실측: Chromium 1243 + D3D11에서 Tint "swizzle view instruction still has usages after lowering" → 로딩에서 멈춤).
// three는 이 실패를 콘솔 오류로만 남기므로 three의 콘솔 함수를 감싸 잡는다. 렌더 루프가 끝내 돌지 않는 경우도 같은 길로 보낸다.
// 시작 화면 뒤(플레이 중) 늦은 파이프라인 실패는 판을 끊지 않고 오류 수집에만 남긴다(src/render/index.ts).
// 다시 열기는 장치 유실과 같은 planGpuRecovery(호환 모드 URL, 상한 2회)를 쓰고, 원인은 세션에 남겨 다음 부팅이 오류 수집으로 보낸다.

export type FallbackCause = 'pipeline' | 'first-frame' | 'device-lost';

export interface FallbackNote { cause: FallbackCause; detail: string; at: number }

/** 다음 부팅이 읽어 오류 수집으로 보내는 세션 키 */
export const FALLBACK_NOTE_KEY = 'dungeonhero.gpuFallback';

/** createRenderer가 돌아온 뒤 화면이 보이는 동안 첫 render 호출을 기다리는 최대 시간(ms) */
export const FIRST_FRAME_TIMEOUT_MS = 40000;

const PIPELINE_FAIL = /pipeline creation failed|Tint IR/i;

export type ConsoleFn = (type: 'log' | 'warn' | 'error', message: string, ...params: unknown[]) => void;

/** three 콘솔 메시지가 WebGPU 파이프라인 생성 실패인지. 맞으면 진단 문자열(앞 300자), 아니면 null */
export function pipelineFailure(type: string, message: unknown): string | null {
  if (type !== 'error' || typeof message !== 'string') return null;
  if (!PIPELINE_FAIL.test(message)) return null;
  return message.replace(/\s+/g, ' ').trim().slice(0, 300);
}

/**
 * three 콘솔 함수를 감싸 파이프라인 생성 실패를 잡는다(처음 한 번만 onFail). 원래 출력은 그대로 낸다.
 * 반환 함수는 감싸기를 푼다.
 */
export function watchPipelineFailures(
  three: { get(): ConsoleFn | null; set(fn: ConsoleFn | null): void },
  onFail: (detail: string) => void,
  sink: Pick<Console, 'log' | 'warn' | 'error'> = console,
): () => void {
  const prev = three.get();
  let fired = false;
  const fn: ConsoleFn = (type, message, ...params) => {
    if (prev) prev(type, message, ...params);
    else (type === 'warn' || type === 'error' ? sink[type] : sink.log)(message, ...params);
    if (fired) return;
    const detail = pipelineFailure(type, message);
    if (detail) { fired = true; onFail(detail); }
  };
  three.set(fn);
  return () => { if (three.get() === fn) three.set(prev); };
}

/**
 * 첫 프레임 감시: 화면이 보이는 시간만 센다(탭이 숨으면 브라우저가 rAF를 멈추므로).
 * tick은 1초 남짓마다 부른다. 첫 프레임이 그려지면 'ok', 보이는 시간이 limit를 넘으면 'timeout'.
 */
export class FirstFrameWatch {
  private visibleMs = 0;
  private last: number;
  private done = false;
  constructor(start: number, private readonly limitMs = FIRST_FRAME_TIMEOUT_MS) { this.last = start; }
  tick(now: number, visible: boolean, framesDrawn: number): 'ok' | 'wait' | 'timeout' {
    if (this.done) return 'ok';
    if (framesDrawn > 0) { this.done = true; return 'ok'; }
    // 한 번에 5초 넘게 비면(탭 정지·기기 잠자기) 그 구간은 세지 않는다
    const dt = now - this.last;
    this.last = now;
    if (visible && dt > 0 && dt < 5000) this.visibleMs += dt;
    if (this.visibleMs >= this.limitMs) { this.done = true; return 'timeout'; }
    return 'wait';
  }
}

interface StoreLike { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

export function saveFallbackNote(store: StoreLike | null, note: FallbackNote): void {
  try { store?.setItem(FALLBACK_NOTE_KEY, JSON.stringify(note)); } catch { /* 저장소 거부: 원인만 잃는다 */ }
}

/** 지난 부팅이 남긴 원인을 읽고 지운다(한 번만 보고) */
export function takeFallbackNote(store: StoreLike | null): FallbackNote | null {
  try {
    const raw = store?.getItem(FALLBACK_NOTE_KEY);
    if (!raw) return null;
    store?.removeItem(FALLBACK_NOTE_KEY);
    const v = JSON.parse(raw) as Partial<FallbackNote>;
    if (v.cause !== 'pipeline' && v.cause !== 'first-frame' && v.cause !== 'device-lost') return null;
    return { cause: v.cause, detail: String(v.detail ?? '').slice(0, 300), at: Number(v.at) || 0 };
  } catch { return null; }
}

/** 오류 수집에 보낼 한 줄(서명이 원인별로 갈리게 원인을 앞에 둔다) */
export function fallbackReport(note: FallbackNote): string {
  return '[gpu-fallback] WebGPU→WebGL2 ' + note.cause + ': ' + note.detail;
}
